import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import SettingsLayout from '../../components/SettingsLayout';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { Save } from 'lucide-react';
import { API_URL } from '../../lib/apiBase';

const ICON_OPTIONS = ['📦', '🚀', '🎯', '💡', '🔥', '⚡', '🌟', '🎈', '🤖', '🎨', '🌿', '🌍'];
const COLOR_OPTIONS = [
  { name: 'Violet',  hex: '#8B5CF6' },
  { name: 'Indigo',  hex: '#6366F1' },
  { name: 'Sky',     hex: '#0EA5E9' },
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Amber',   hex: '#F59E0B' },
  { name: 'Rose',    hex: '#F43F5E' },
];

const BrandingPage = () => {
  const { currentWorkspace, refreshUsage } = useWorkspace();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('📦');
  const [color, setColor] = useState('#8B5CF6');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentWorkspace) return;
    setName(currentWorkspace.name || '');
    setIcon(currentWorkspace.icon || '📦');
    setColor(currentWorkspace.color || '#8B5CF6');
  }, [currentWorkspace]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentWorkspace) return;
    if (!name.trim()) {
      toast.error('Workspace name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/workspaces/${currentWorkspace.id}`, {
        name: name.trim(),
        icon,
        color,
      });
      toast.success('Branding saved');
      if (typeof refreshUsage === 'function') refreshUsage();
    } catch (_e) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout title="Branding" description="Change your workspace name, icon, and accent color.">
      <form onSubmit={handleSave} className="space-y-6" data-testid="branding-form">
        <Card className="p-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="ws-name">Workspace name</Label>
            <Input
              id="ws-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. SoloSub"
              maxLength={100}
              data-testid="branding-name-input"
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Icon</Label>
            <div className="flex flex-wrap gap-2" data-testid="branding-icon-grid">
              {ICON_OPTIONS.map((emoji) => (
                <button
                  type="button"
                  key={emoji}
                  onClick={() => setIcon(emoji)}
                  aria-pressed={icon === emoji}
                  className={`h-10 w-10 rounded-md border text-lg flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    icon === emoji
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                      : 'hover:bg-muted'
                  }`}
                  data-testid={`branding-icon-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Accent color</Label>
            <div className="flex flex-wrap gap-2" data-testid="branding-color-grid">
              {COLOR_OPTIONS.map((c) => (
                <button
                  type="button"
                  key={c.hex}
                  onClick={() => setColor(c.hex)}
                  aria-pressed={color === c.hex}
                  className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                    color === c.hex
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                      : 'hover:bg-muted'
                  }`}
                  data-testid={`branding-color-${c.name.toLowerCase()}`}
                >
                  <span
                    className="h-3.5 w-3.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: c.hex }}
                    aria-hidden="true"
                  />
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="btn-cta" data-testid="branding-save">
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </SettingsLayout>
  );
};

export default BrandingPage;
